import { useState } from 'react'
import { FolderTree, LogIn } from 'lucide-react'
import { Button } from '@/components/ui'

interface Props {
  /** When true, a prior session expired mid-use. */
  expired?: boolean
  /** Heading text. Defaults to the Uviva vendor portal copy. */
  title?: string
  /** Subtitle shown under the heading (non-expired state only). */
  subtitle?: string
  /** Where to send the browser after a successful login. */
  landingPath?: string
}

/**
 * Email + password login screen for password-auth ("vendor-flagged")
 * roles — Uviva today, outside_sales_rep since 2026-07-21. Replaces the
 * Google OAuth screen when the URL path matches one of Shell.tsx's
 * vendor login path prefixes.
 *
 * Posts to /api/auth/vendor-login → receives carla_ui_session cookie
 * on success → redirects to `landingPath`. The endpoint itself is
 * role-agnostic (gated only on the caller's `vendor` rbac flag), so no
 * backend change was needed to support a second vendor-flagged role.
 */
export function VendorLoginScreen({
  expired = false,
  title = 'Uviva Portal',
  subtitle = 'SPI partner portal — Uviva accounts only.',
  landingPath = '/uviva',
}: Props) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showReset, setShowReset] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  const [resetEmail, setResetEmail] = useState('')

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await fetch('/api/auth/vendor-login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      })
      if (res.ok) {
        window.location.href = landingPath
      } else {
        const body = await res.json().catch(() => ({}))
        setError(
          res.status === 401
            ? 'Invalid email or password.'
            : (body.detail as string | undefined) ?? 'Login failed. Please try again.',
        )
      }
    } catch {
      setError('Network error. Please check your connection and try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handleResetRequest(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await fetch('/api/auth/vendor-reset-request', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail.trim() }),
      })
      if (res.ok || res.status === 404) {
        // Always show success to avoid email enumeration
        setResetSent(true)
      } else {
        setError('Could not send reset email. Please try again.')
      }
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (showReset) {
    return (
      <div className="flex h-dvh items-center justify-center bg-canvas">
        <div className="flex w-full max-w-sm flex-col gap-6 rounded-md border border-border-subtle bg-surface px-8 py-10 shadow-sm">
          <div className="flex flex-col items-center gap-3">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-sm bg-accent text-on-accent">
              <FolderTree size={24} aria-hidden="true" />
            </span>
            <h1 className="text-xl font-semibold text-primary">Reset password</h1>
          </div>

          {resetSent ? (
            <div className="flex flex-col gap-4 text-center">
              <p className="text-sm text-secondary">
                If that address is registered, you'll receive a reset link within a
                few minutes. Check your inbox.
              </p>
              <Button variant="ghost" size="sm" onClick={() => { setShowReset(false); setResetSent(false) }}>
                Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleResetRequest} className="flex flex-col gap-4">
              {error && (
                <p className="rounded border border-danger-border bg-danger-subtle px-3 py-2 text-sm text-danger">
                  {error}
                </p>
              )}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="reset-email" className="text-sm font-medium text-primary">
                  Email address
                </label>
                <input
                  id="reset-email"
                  type="email"
                  autoComplete="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="rounded border border-border bg-surface px-3 py-2 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
                  required
                />
              </div>
              <Button type="submit" variant="primary" disabled={loading} className="w-full">
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowReset(false)}>
                Back to sign in
              </Button>
            </form>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-dvh items-center justify-center bg-canvas">
      <div className="flex w-full max-w-sm flex-col gap-6 rounded-md border border-border-subtle bg-surface px-8 py-10 shadow-sm">
        <div className="flex flex-col items-center gap-3">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-sm bg-accent text-on-accent">
            <FolderTree size={24} aria-hidden="true" />
          </span>
          <div className="flex flex-col items-center gap-1">
            <h1 className="text-xl font-semibold text-primary">{title}</h1>
            <p className="text-sm text-secondary">
              {expired ? 'Your session expired. Sign in again.' : subtitle}
            </p>
          </div>
        </div>

        <form onSubmit={handleLogin} className="flex flex-col gap-4">
          {error && (
            <p className="rounded border border-danger-border bg-danger-subtle px-3 py-2 text-sm text-danger">
              {error}
            </p>
          )}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="vendor-email" className="text-sm font-medium text-primary">
              Email
            </label>
            <input
              id="vendor-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded border border-border bg-surface px-3 py-2 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="vendor-password" className="text-sm font-medium text-primary">
              Password
            </label>
            <input
              id="vendor-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded border border-border bg-surface px-3 py-2 text-sm text-primary outline-none focus:ring-2 focus:ring-accent"
              required
            />
          </div>

          <Button type="submit" variant="primary" disabled={loading} className="w-full">
            <LogIn size={14} />
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <button
          type="button"
          onClick={() => { setShowReset(true); setResetEmail(email) }}
          className="text-center text-sm text-link hover:underline"
        >
          Forgot password?
        </button>
      </div>
    </div>
  )
}
